import { SnLonLat, SnLonLatLike } from '@lib/geo/helpers';
import { IMapMarkerStyleOptions } from '@lib/geo/map/core/marker/marker-options';
import { SnBadgeConfig } from '@lib/ui/badge';

import { EsriMap } from '../../../map';
import { IEsriMapMarkerLabel, EsriMapMarkerIcon, IEsriMapMarkerIconLabel } from '../marker-options';
import { EsriMarkerType } from '../marker-types';

import esri = __esri;

/**
 * Common interface for ESRI Map markers that can be used by both
 * EsriMapMarker (deprecated) and FsAdvancedEsriMarker (modern)
 */
export interface IEsriMarker {
  // Core methods
  getMarker(): esri.Graphic;
  addTo(map: EsriMap): void;
  remove(): void;

  // Position methods
  getLonLat(): SnLonLat;
  setLonLat(lonLat: SnLonLatLike): void;

  // Title methods
  getTitle(): string;
  setTitle(title: string): void;

  // Draggable methods
  getDraggable(): boolean;
  setDraggable(draggable: boolean): void;

  // Z-index methods
  getZIndex(): number;
  setZIndex(zIndex: number): void;

  // Icon methods
  getIcon(): EsriMapMarkerIcon | undefined;
  setIcon(icon: EsriMapMarkerIcon): void;

  // Style methods
  getStyle(): IMapMarkerStyleOptions | undefined;
  setStyle(style: IMapMarkerStyleOptions): void;

  // Badge methods
  getBadge(): SnBadgeConfig | undefined;
  setBadge(badge: SnBadgeConfig): void;

  // Label methods
  getLabel(): IEsriMapMarkerLabel | undefined;
  setLabel(label: string | IEsriMapMarkerLabel): void;

  // Rotation methods
  getRotation(): number;
  setRotation(rotation: number): void;

  // Additional properties that might be needed
  feature?: esri.Graphic | unknown;
  timeouts?: NodeJS.Timeout[];
  interval?: number;
  pulseInterval?: number;
  iconLabel?: IEsriMapMarkerIconLabel;
  address?: string;

  // Advanced methods that some services might need
  setType?(type: EsriMarkerType): void;
  getType?(): EsriMarkerType | undefined;
  getFeatures?(): unknown;
  setPulse?(colors: string[], delay: number, fontSet?: string, fontIcon?: string): void;
  stopPulse?(): void;
  setAddress?(address: string): void;
  getAddress?(): string | undefined;
  setFeature?(feature: unknown): void;
  getFeature?(): unknown;
  setIconAnchor?(anchor: SnLonLatLike): void;
  getIconAnchor?(): SnLonLat | undefined;
  setCustomData?(data: unknown): void;
  getCustomData?(): unknown;
  setStatusBadge?(status: string, badgePosition?: Record<string, unknown>): void;
  getStatusBadge?(): { status?: string; position?: Record<string, unknown> };
  clearStatusBadge?(): void;

  // Visibility methods
  setVisible?(visible: boolean): void;
  getVisible?(): boolean;

  // Geometry methods
  setFollowMode?(followMode: boolean): void;
  getFollowMode?(): boolean;
  setDestination?(dest: SnLonLatLike): void;
  setDeltas?(seconds: number): void;
  move?(): void;
  animateSetLonLat?(seconds: number, moveto: SnLonLatLike): void;
  isWithinMapBounds?(): boolean;
  getAnimationState?(): { isAnimating: boolean; destination?: SnLonLat; seconds?: number };
}
